/**
 * Organization Context - Optimized with React Query
 *
 * PERFORMANCE FIX: Eliminates "Context Waterfall" anti-pattern
 *
 * **Before (Waterfall):**
 * T=0ms    Auth starts
 * T=200ms  Auth completes, user available
 * T=200ms  Org fetch STARTS (wasted 200ms waiting!)
 * T=400ms  Org fetch completes
 *
 * **After (Parallel-Ready):**
 * T=0ms    Auth starts, Org query enabled (waiting for user)
 * T=200ms  Auth completes → Org query instantly starts (no delay)
 * T=400ms  Org fetch completes
 *
 * React Query's `enabled` option means the query is "hot" and
 * starts immediately when the condition becomes true - no useEffect delay.
 *
 * @version 2.0.0
 * @date 2025-12-29
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import {
  getUserOrganizations,
  getOrganization,
  getCurrentUserMembership,
} from '@/services/organizationService';
import type { Organization, OrganizationMember } from '@/types/organization';
import { logger } from '@/utils/logger';
import { setPrivate, getPrivate, STORAGE_KEYS } from '@/lib/secureStorage';

interface OrganizationContextType {
  // Current organization
  currentOrganization: Organization | null;
  currentMembership: OrganizationMember | null;

  // All organizations user belongs to
  userOrganizations: Organization[];

  // Loading states
  isLoading: boolean;
  error: Error | null;

  // Actions
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshOrganization: () => Promise<void>;
  refreshMembership: () => Promise<void>;

  // Permissions helpers
  hasPermission: (permission: keyof OrganizationMember['permissions']) => boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const STORAGE_KEY = STORAGE_KEYS.ORG_ID;

// Query keys for React Query cache management
const orgQueryKeys = {
  all: ['organizations'] as const,
  userOrgs: (userId: string) => [...orgQueryKeys.all, 'user', userId] as const,
  single: (orgId: string) => [...orgQueryKeys.all, 'single', orgId] as const,
  membership: (orgId: string, userId: string) => [...orgQueryKeys.all, 'membership', orgId, userId] as const,
};

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();

  // CRITICAL: For RLS queries to work, we need BOTH:
  // 1. user in React state (for user.id)
  // 2. session in React state (proves JWT is available for requests)
  const isAuthReady = !!user?.id && !!session;

  // Track which org is selected (only the ID, not the full object)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => {
    // Initialize from secure storage immediately (sync, no fetch)
    return getPrivate(STORAGE_KEY);
  });

  // PERFORMANCE FIX: React Query with `enabled` starts fetch INSTANTLY when user becomes available
  // No useEffect delay - query is "hot" and waiting for the condition
  //
  // CRITICAL FIX (2025-12-30): Query must wait for BOTH user AND session
  // If we query when user exists but session doesn't, auth.uid() returns NULL
  // and RLS policies block everything → 0 organizations returned
  const {
    data: userOrganizations = [],
    isLoading: isLoadingOrgs,
    error: orgsError,
    refetch: refetchOrgs,
  } = useQuery({
    queryKey: orgQueryKeys.userOrgs(user?.id ?? ''),
    queryFn: async () => {
      if (!user?.id) return [];
      const startTime = Date.now();
      console.log(`🏢 [PERF] OrganizationContext getUserOrganizations started at ${new Date().toISOString().split('T')[1]}`);
      console.log(`🏢 [DEBUG] isAuthReady=${isAuthReady}, hasUser=${!!user}, hasSession=${!!session}`);
      logger.info('[OrgContext] Fetching user organizations...');
      const orgs = await getUserOrganizations(user.id);
      console.log(`🏢 [PERF] OrganizationContext getUserOrganizations completed in ${Date.now() - startTime}ms (${orgs.length} orgs)`);

      // Auto-select first org if none selected or selected org no longer available
      if (orgs.length > 0) {
        const storedId = getPrivate(STORAGE_KEY);
        const validStoredOrg = storedId && orgs.some(o => o.id === storedId);

        if (!validStoredOrg) {
          // Select first org and persist
          setSelectedOrgId(orgs[0].id);
          setPrivate(STORAGE_KEY, orgs[0].id);
        } else if (storedId && !selectedOrgId) {
          // Restore from storage
          setSelectedOrgId(storedId);
        }
      }

      return orgs;
    },
    // CRITICAL: Must wait for session to be available, not just user
    // Without session, Supabase won't send JWT → auth.uid() is NULL → RLS blocks all
    enabled: isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes - orgs don't change often
    gcTime: 15 * 60 * 1000, // 15 minutes cache retention
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Determine the effective org ID (from state or first available)
  // BUGFIX: Also consider the org from userOrganizations directly to avoid race conditions
  // where selectedOrgId hasn't been updated yet but userOrganizations has data
  const effectiveOrgId = useMemo(() => {
    // First check if selectedOrgId is valid in the current org list
    if (selectedOrgId && userOrganizations.some(o => o.id === selectedOrgId)) {
      return selectedOrgId;
    }
    // Fall back to first org if we have orgs loaded
    if (userOrganizations.length > 0) {
      return userOrganizations[0].id;
    }
    // Last resort: check storage directly (handles initial render before query completes)
    const storedId = getPrivate(STORAGE_KEY);
    if (storedId) {
      return storedId;
    }
    return null;
  }, [selectedOrgId, userOrganizations]);

  // OPTIMIZATION: Use organization from userOrganizations if available (avoids extra query)
  // Fall back to dedicated query only if needed (e.g., when org details are stale)
  const orgFromList = useMemo(() => {
    if (!effectiveOrgId) return null;
    return userOrganizations.find(o => o.id === effectiveOrgId) ?? null;
  }, [effectiveOrgId, userOrganizations]);

  // Fetch current organization details (only if not available from userOrganizations)
  const {
    data: fetchedOrganization = null,
    isLoading: isLoadingOrg,
    error: orgError,
  } = useQuery({
    queryKey: orgQueryKeys.single(effectiveOrgId ?? 'none'),
    queryFn: async () => {
      if (!effectiveOrgId) {
        console.warn('[OrgContext] No effectiveOrgId available - cannot fetch organization');
        return null;
      }
      const startTime = Date.now();
      console.log(`🏢 [PERF] OrganizationContext getOrganization started for ${effectiveOrgId}`);
      logger.info('[OrgContext] Fetching organization details:', effectiveOrgId);
      const org = await getOrganization(effectiveOrgId);
      console.log(`🏢 [PERF] OrganizationContext getOrganization completed in ${Date.now() - startTime}ms:`, org?.name || 'null');
      return org;
    },
    // Only fetch if we don't already have the org from userOrganizations
    // CRITICAL: Also requires session for RLS to work
    enabled: isAuthReady && !!effectiveOrgId && !orgFromList,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Use org from list (faster) or fetched org (fallback)
  const currentOrganization = orgFromList ?? fetchedOrganization;

  // Fetch current membership (parallel with org details)
  const {
    data: currentMembership = null,
    isLoading: isLoadingMembership,
    error: membershipError,
  } = useQuery({
    queryKey: orgQueryKeys.membership(effectiveOrgId ?? '', user?.id ?? ''),
    queryFn: async () => {
      if (!effectiveOrgId || !user?.id) return null;
      logger.info('[OrgContext] Fetching membership for org:', effectiveOrgId);
      return getCurrentUserMembership(effectiveOrgId, user.id);
    },
    // CRITICAL: Must wait for session to ensure RLS works
    enabled: isAuthReady && !!effectiveOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Combined loading state
  // Note: Only consider org loading if we don't already have the org from userOrganizations
  const isLoading = isLoadingOrgs || (!orgFromList && isLoadingOrg) || isLoadingMembership;

  // Combined error state
  const error = orgsError || orgError || membershipError || null;

  // Switch to a different organization
  const switchOrganization = useCallback(async (organizationId: string) => {
    if (!user) return;

    try {
      // Update state immediately for responsiveness
      setSelectedOrgId(organizationId);
      setPrivate(STORAGE_KEY, organizationId);

      // Invalidate queries to refetch with new org ID
      // React Query will handle the refetch automatically
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orgQueryKeys.single(organizationId) }),
        queryClient.invalidateQueries({ queryKey: orgQueryKeys.membership(organizationId, user.id) }),
      ]);
    } catch (err) {
      logger.error('Failed to switch organization:', err);
      throw err;
    }
  }, [user, queryClient]);

  // Refresh current organization data
  const refreshOrganization = useCallback(async () => {
    if (!effectiveOrgId) return;
    await queryClient.invalidateQueries({ queryKey: orgQueryKeys.single(effectiveOrgId) });
  }, [effectiveOrgId, queryClient]);

  // Refresh current membership data
  const refreshMembership = useCallback(async () => {
    if (!effectiveOrgId || !user?.id) return;
    await queryClient.invalidateQueries({
      queryKey: orgQueryKeys.membership(effectiveOrgId, user.id)
    });
  }, [effectiveOrgId, user?.id, queryClient]);

  // Check if user has a specific permission
  const hasPermission = useCallback(
    (permission: keyof OrganizationMember['permissions']): boolean => {
      if (!currentMembership) return false;
      return currentMembership.permissions[permission];
    },
    [currentMembership]
  );

  // Role checks (memoized to prevent unnecessary re-renders)
  // Note: 'owner' is the highest role (organization creator) and has all admin privileges
  const isAdmin = currentMembership?.role === 'admin' || currentMembership?.role === 'owner';
  const isEditor = currentMembership?.role === 'editor' || currentMembership?.role === 'member';
  const isViewer = currentMembership?.role === 'viewer';

  // Memoize value to prevent re-renders when nothing changed
  const value = useMemo<OrganizationContextType>(() => ({
    currentOrganization,
    currentMembership,
    userOrganizations,
    isLoading,
    error: error as Error | null,
    switchOrganization,
    refreshOrganization,
    refreshMembership,
    hasPermission,
    isAdmin,
    isEditor,
    isViewer,
  }), [
    currentOrganization,
    currentMembership,
    userOrganizations,
    isLoading,
    error,
    switchOrganization,
    refreshOrganization,
    refreshMembership,
    hasPermission,
    isAdmin,
    isEditor,
    isViewer,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

// Hook to use organization context
export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

// Hook to ensure user is an admin (kept for backward compatibility)
export const useRequireAdmin = (): void => {
  const { isAdmin, currentMembership } = useOrganization();

  // Note: This throws during render which is an anti-pattern
  // Consider using a redirect or error boundary instead
  if (currentMembership && !isAdmin) {
    throw new Error('Admin access required');
  }
};

// Hook to ensure user has a specific permission
export const useRequirePermission = (
  permission: keyof OrganizationMember['permissions']
): void => {
  const { hasPermission, currentMembership } = useOrganization();

  if (currentMembership && !hasPermission(permission)) {
    throw new Error(`Permission required: ${permission}`);
  }
};
